import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { Download, HardDrive } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { EmptyState, SkeletonRows } from "@/components/app/Common";
import { api, get, errMsg } from "@/lib/api";
import { download } from "@/lib/fit";

const KIND = { legacy_unshredded: "Readable until purged", crypto_shredded: "Key destroyed (unreadable)" };

export default function StoragePurge() {
  const qc = useQueryClient();
  const [status, setStatus] = useState("pending");
  const { data, isLoading } = useQuery({ queryKey: ["storage-purge", status], queryFn: () => get("/settings/storage-purge", { status }) });
  const mark = async (id) => {
    try { await api.post(`/settings/storage-purge/${id}/purged`); toast.success("Marked as purged"); qc.invalidateQueries({ queryKey: ["storage-purge"] }); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <section className="panel overflow-hidden" data-testid="storage-purge-page">
      <div className="flex flex-wrap items-center gap-3 border-b border-line px-6 py-4">
        <div className="min-w-0 flex-1"><h2 className="text-lg font-semibold">Storage purge</h2>
          <p className="text-sm text-ink-secondary">File storage has no delete, so erased candidates' files are listed here. Delete each object from storage, then mark it purged. Older files stay readable until purged.</p></div>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="h-9 rounded-lg border border-line bg-white px-2 text-sm" aria-label="Filter" data-testid="storage-purge-filter">
          <option value="pending">Pending</option><option value="purged">Purged</option><option value="all">All</option></select>
        <Button size="sm" variant="outline" onClick={() => download("/settings/storage-purge/export", "pending-storage-purge.csv")} data-testid="storage-purge-export-btn"><Download size={14} /> Export CSV</Button>
      </div>
      {isLoading ? <SkeletonRows rows={4} /> : !data.length ? <EmptyState icon={HardDrive} title="Nothing to purge" text="Files from erased candidates appear here." testId="storage-purge-empty" /> : (
        <div className="overflow-x-auto"><table className="w-full min-w-[640px] text-sm" data-testid="storage-purge-table">
          <thead className="bg-subtle text-left text-xs text-ink-muted"><tr><th className="px-6 py-2 font-medium">Object path</th><th className="px-4 py-2 font-medium">State</th><th className="px-4 py-2 font-medium">Reason</th><th className="px-4 py-2 font-medium">Listed</th><th className="px-6 py-2" /></tr></thead>
          <tbody className="divide-y divide-line">{data.map((d) => (
            <tr key={d.id} data-testid={`storage-purge-row-${d.id}`}>
              <td className="max-w-[320px] truncate px-6 py-2 font-mono text-xs" title={d.path}>{d.path}</td>
              <td className="px-4 py-2 text-xs">{KIND[d.kind] || d.kind}</td>
              <td className="px-4 py-2 text-xs text-ink-secondary">{d.reason}</td>
              <td className="px-4 py-2 text-xs text-ink-muted">{d.created_at ? format(new Date(d.created_at), "d MMM yyyy") : "—"}</td>
              <td className="px-6 py-2 text-right">{d.purged_at ? <span className="text-xs text-ink-muted">Purged {format(new Date(d.purged_at), "d MMM")}</span>
                : <Button size="sm" variant="outline" className="h-8" onClick={() => mark(d.id)} data-testid={`storage-purge-mark-${d.id}`}>Mark purged</Button>}</td>
            </tr>))}</tbody>
        </table></div>
      )}
    </section>
  );
}
